/** Static UI translations — no backend API required. */
export const translations = {
  en: {
    sidebar: {
      dashboard: "Dashboard",
      installationMap: "Installation Info",
      environ: "Environ",
      monitoring: "Monitoring",
      adc: "ADC",
      logs: "Logs",
      report: "Report",
      settings: "Settings",
      aiModel: "AI Model (Beta)"
    },
    common: {
      connecting: "Connecting...",
      checking: "Checking...",
      normal: "Normal",
      enter: "Enter",
      cancel: "Cancel",
      connect: "Connect"
    },
    connection: {
      connecting: "Connecting to live data…",
      reconnecting: "Reconnecting to live data…",
      offline: "You are offline",
      recovered: "Live data restored",
      lastUpdate: "Last update"
    },
    settings: {
      title: "System Configuration",
      subtitle: "Manage protocol, sound, language, appearance, and access settings.",
      language: "Language",
      appearance: "Appearance",
      darkMode: "Dark mode",
      lightMode: "Light mode",
      themeChanged: "Theme updated.",
      tabs: { general: "General", wifi: "WiFi", tcpip: "TCP/IP", modbus: "Modbus", installation: "Installation Info" },
      activeProtocol: "Active Protocol",
      configureActivate: "Configure & Activate Protocol",
      activating: "Activating...",
      currentProtocol: "Current Protocol",
      noneSelected: "None Selected",
      active: "(Active)",
      inactive: "(Inactive)",
      alertSound: "Alert Sound",
      saveSound: "Save Sound",
      saving: "Saving...",
      applicationAccess: "Application Access",
      locating: "Locating...",
      copied: "Copied!",
      selectProtocolFirst: "Please select a protocol first.",
      saveConfig: "Save Config",
      saveConfigSuccess: "WiFi configuration saved.",
      soundSaved: "Sound preference saved.",
      languageChanged: "Language updated.",
      manual: "Manual",
      autoDhcp: "Auto (DHCP)",
      ipAddress: "IP Address",
      systemIp: "System IP",
      activatingDialogTitle: "Activating Protocol",
      activatingDialogText: "Connecting and applying configuration...",
      companyLogo: "Company Logo",
      companyLogoHint: "Upload your company logo to show on the report page and PDF exports.",
      companyLogoUpload: "Upload logo",
      companyLogoRemove: "Remove logo",
      companyLogoUploading: "Uploading…",
      companyLogoUploaded: "Company logo saved. It will be retained the next time the program is run.",
      companyLogoRemoved: "Company logo removed.",
      companyLogoFailed: "Could not update company logo.",
      companyLogoFormats: "PNG, JPG, GIF, BMP, or WEBP · max recommended 400×120 px"
    },
    installation: {
      title: "Installed H-LINK & Sensor Information",
      subtitle: "Enter installation location and management IDs. Data is stored on this PC and shown on Dashboard and Monitoring.",
      subtitleShort: "Write installation data per H-LINK and sensor — saved locally, shown on Dashboard & Monitoring.",
      writeFunctionNote: "Write Function: Enter and save installation-specific information for each H-LINK and sensor device at initial setup. Fields include installation location, position coordinates, map/mark info, device management number, and other identification (remarks).",
      installationLocation: "Installation Location",
      managementNumber: "Device Management Number",
      colMgmtNumber: "Mgmt No.",
      mgmtPlaceholder: "e.g. MGT-001",
      addHlinkDialogTitle: "Add H-LINK",
      addHlinkDialogDesc: "Enter the H-LINK serial number from the device label. You can add sensors and installation details before the unit is online.",
      addHlinkSerialPlaceholder: "Enter H-LINK S/N",
      serialRequired: "Serial number is required.",
      hlinkExists: "This H-LINK serial number is already in the list.",
      hlinkAdded: "H-LINK added. Add sensors and click Write All Information to save.",
      sensorAdded: "Sensor {id} added.",
      sensorLimit: "Maximum 12 sensors per H-LINK.",
      removeHlink: "Remove H-LINK",
      clearHlink: "Clear all fields",
      removeHlinkConfirm: "Remove H-LINK {serial} from Installation Info? This cannot be undone.",
      removeHlinkSuccess: "H-LINK removed from Installation Info.",
      cannotRemoveLive: "Connected H-LINK cannot be removed. Disconnect it first, or clear its fields.",
      noDevices: "No H-LINK devices detected. Connect devices and wait for live data, then return here.",
      connected: "Connected",
      disconnected: "Disconnected",
      serialReadOnly: "H-LINK S/N",
      serialNotEditable: "Serial number cannot be changed",
      hlinkId: "H-LINK S/N (read-only)",
      hlinkWriteId: "H-LINK ID (Write)",
      installInfo: "Installation Information",
      installDate: "Installation Date",
      remarks: "Remarks",
      sensorTableTitle: "Sensors connected to H-LINK #{index}",
      colNo: "No.",
      colSensorId: "Sensor ID",
      colType: "Sensor Type",
      colInstallInfo: "Installation Information",
      colRemarks: "Remarks",
      colStatus: "Status",
      footerNote: "Up to 12 sensors can be connected to one H-LINK. Information entered here is displayed on the Dashboard and Monitoring screens.",
      footerCompact: "Sensors are detected automatically from connected H-LINK. Max 12 per H-LINK. Data appears on Dashboard & Monitoring. S/N is read-only.",
      connectedTotal: "Total connected sensors: {count}",
      sensorsConnected: "Sensors (Connected: {connected} / Max: {max})",
      writeAll: "Write All Information",
      saving: "Saving…",
      saveSuccess: "Installation information saved.",
      saveFailed: "Could not save installation information.",
      locationLabel: "Location",
      hlinkSection: "H-LINK Installation Details",
      locationPlaceholder: "e.g. Lobby, Line A",
      loading: "Loading installation data…",
      statHlinks: "H-LINK Units",
      statSensors: "Connected Sensors",
      noDevicesTitle: "No devices detected",
      maxChars: "(Max {max} characters)",
      remarksPlaceholder: "Other identification / notes",
      editHlink: "Edit H-LINK Info",
      delete: "Delete",
      sensorCount: "Sensors: {count}",
      addSensor: "Add Sensor",
      addHlink: "Add H-LINK",
      addHlinkHint: "Add H-LINK by serial number for initial installation setup",
      colAction: "Action",
      editRow: "Edit",
      clearRow: "Clear fields",
      noteTitle: "Note",
      noteDashboard: "The information entered here will be displayed in the Dashboard and Monitoring menu.",
      noteSerial: "Serial Number (S/N) is read-only and cannot be changed.",
      positionCoords: "Position Coordinates",
      mapMarkInfo: "Map / Mark Info",
      mapMarkPlaceholder: "e.g. Zone A, Mark-3",
      mapMarkAutoPlaceholder: "Auto from coordinates",
      mapMarkAutoHint: "Map mark is created automatically from Position Coordinates (x, y).",
      uploadMap: "Upload floor map",
      mapUploaded: "Floor map uploaded.",
      noMapYet: "No map image uploaded",
      draft: "Draft",
      collapseAll: "Collapse all",
      waitingSensors: "Waiting for sensors from connected H-LINK…",
      snMismatchHint: "No saved install info for this S/N — enter via Settings → Installation Info.",
      mapUploadHint: "Floor map stored for this H-LINK (optional).",
      envSensorsHint: "Environment sensor info is shown on Monitoring cards only — not on the Installation Map.",
    },
    installMap: {
      title: "Installation Map View",
      subtitle: "H-LINK floor plans with sensor positions from saved installation data.",
      subtitleWorld: "Point each H-LINK on the world map to mark where the device is installed.",
      subtitle3d: "3D floor view with live sensor markers from installation settings.",
      viewWorld: "World Map",
      view2d: "2D",
      view3d: "3D",
      worldHint: "Select an H-LINK in the list, then click the world map to place it.",
      worldHintPlace: "Click the map to set the installation point for S/N {serial}. Drag a pin to move it.",
      worldCoords: "World coordinates",
      clearPin: "Remove pin",
      worldPlaced: "Pinned on world map",
      worldNotPlaced: "Not pinned yet",
      controls3d: "Drag to rotate · Scroll to zoom · Click a sensor marker to select",
      noDevices: "No installation maps yet. Add H-LINK details and upload a floor map in Settings → Installation Info.",
      noMapImage: "No floor map image",
      uploadInSettings: "Upload a floor map in Settings → Installation Info for this H-LINK.",
      sensorDetails: "Selected Sensor",
      selectSensorHint: "Click a sensor on the map or in the list below to view details.",
      clearSelection: "Clear selection",
      sensorsByHlink: "Sensors by H-LINK",
      hlinkSensors: "{count} sensor(s)",
      allSensors: "All Sensors",
      noSensors: "No sensors on this H-LINK.",
      noMark: "No mark info",
      fieldId: "Sensor ID",
      fieldLocation: "Installation Location",
      fieldCoords: "Coordinates",
      fieldMark: "Map / Mark Info",
      fieldMgmt: "Management No.",
      fieldStatus: "Status",
      fieldTemp: "Temperature"
    },
    arc: {
      emergency: "EMERGENCY",
      critical: "CRITICAL",
      warning: "WARNING",
      eWarning: "E-WARNING",
      noRisk: "NO Risk",
      normal: "NORMAL",
      severe: "SEVERE",
      danger: "DANGER",
      caution: "CAUTION"
    },
    dashboard: {
      pageTitle: "e-HAECHIE Sensor Real-time Monitoring",
      arcFault: "ARC FAULT",
      temperature: "TEMPERATURE",
      max: "Max",
      avg: "Avg",
      min: "Min",
      totalOperationStatus: "Total Operation Status",
      lineOperation: "Line Operation",
      sensorOperation: "Sensor Operation",
      anomalyDetection: "Anomaly Detection",
      check: "Check",
      sensorDeviceStatus: "Sensor Device Status",
      enterDeviceId: "Enter Device ID",
      deviceId: "Device_ID",
      lora: "LoRA",
      status: "Status",
      noDataAvailable: "No data available",
      sensingStatus: "Sensing Status",
      noRisk: "No Risk",
      emergency: "Emergency",
      critical: "Critical",
      warning: "Warning",
      eventLogs: "Event Logs",
      time: "Time",
      arcLevel: "ARC Level",
      sensorStatus: "SENSOR STATUS",
      waitingDevices: "Waiting for device data...",
      resetWarnings: "Reset Warnings",
      anomalyInsight: "Anomaly Insight",
      thresholdConfig: "Threshold Config"
    },
    monitoring: {
      pageTitle: "e-HAECHIE Sensor Real-time Monitoring",
      arcFault: "ARC FAULT",
      arcFaultLabel: "Arc Fault",
      normalLabel: "Normal",
      temperature: "TEMPERATURE",
      deviceSerial: "Device Serial",
      irTempSensors: "IR & Temperature Sensors",
      envSensors: "Environment Sensors",
      noSensors: "No sensors connected.",
      resetWarnings: "Reset Warnings",
      anomalyInsight: "Anomaly Insight",
      thresholdConfig: "Threshold Config"
    },
    adc: {
      pageTitle: "e-HAECHIE Sensor Real-time Monitoring",
      downloadReport: "Download Report",
      waitingDevices: "No Active H-Link Devices Connected",
      arcLevel: "Arc Level",
      maxValue: "Max Value",
      waitingSensorData: "Waiting for Sensor Data...",
      selectDeviceTab: "Select an active device tab or check connection.",
      downloading: "Downloading...",
      download: "Download"
    },
    environ: {
      pageTitle: "Environment Dashboard",
      charts: "Charts",
      table: "Table",
      noDevices: "No devices connected",
      waitingSensors: "Waiting for sensor data...",
      viewingAll: "Viewing all sensors",
      from: "From",
      to: "To",
      loadingData: "Loading data...",
      sensorId: "Sensor ID",
      temp: "Temp (°C)",
      humidity: "Humidity (%)",
      pressure: "Pressure (hPa)",
      eco2: "eCO2 (ppm)",
      iaq: "IAQ",
      pm1: "PM1.0",
      pm25: "PM2.5",
      pm10: "PM10",
      noData: "No data for selected range"
    },
    logs: {
      title: "Event Logs",
      arcSection: "Arc Events",
      tempSection: "Temperature Events",
      no: "No.",
      date: "Date",
      time: "Time",
      sensorId: "Sensor ID",
      event: "Event",
      arcLevel: "ARC Level",
      detail: "Detail",
      tempDetail: "Temp / Threshold",
      action: "Action",
      arcDetection: "ARC Detection",
      tempOverheat: "Temp Overheat",
      noEvents: "No events yet",
      viewGraph: "Details",
      cycleGraphTitle: "Temperature cycle",
      temperature: "Temperature",
      threshold: "Threshold",
      tempCol: "Temp",
      thrCol: "Thr",
      cycleActive: "Still over threshold — updating…",
      cycleEnded: "Returned to normal at",
      cycleLoading: "Loading cycle…",
      cycleFailed: "Could not load cycle data.",
      cycleMissing: "No cycle data for this event yet. Cycles are recorded for new overheat events after the backend update.",
      cycleNoSamples: "No samples recorded for this cycle.",
      delete: "Delete",
      sensor: "Sensor",
      arcData: "ARC Data",
      confirmDeletion: "Confirm Deletion",
      confirmDeleteMessage: "Are you sure you want to delete the log for sensor",
      at: "at",
      logDeleted: "Log deleted successfully",
      deleteFailed: "Failed to delete log",
      wsError: "WebSocket connection error."
    },
    report: {
      pageTitle: "HAECHIE Sensor Monitoring Report",
      pageSubtitle: "Interactive analytics dashboard — export a formal PDF report anytime.",
      configureSections: "Configure Sections",
      refresh: "Refresh",
      downloadPdf: "Download Report (PDF)",
      downloadReport: "Download Report",
      downloadAsPdf: "Download as PDF",
      downloadAsExcel: "Download as Excel",
      emailReport: "Email Report",
      generateReport: "Download PDF",
      generateReportPdf: "Download PDF",
      generateReportExcel: "Download Excel",
      exporting: "Exporting...",
      exportFailed: "Failed to export PDF. Please try again.",
      exportExcelFailed: "Failed to export Excel. Please try again.",
      emailModalTitle: "Email report",
      emailModalHint: "The sensor report PDF is attached automatically when sent via server or your device's share menu.",
      emailAttachment: "Attachment",
      emailAttachmentPreparing: "Generating report PDF…",
      emailAttachmentReady: "Ready to attach",
      emailAttachmentMissing: "Could not generate PDF — close and try again.",
      emailShareSent: "Report shared with PDF attached. Complete sending in your email app.",
      emailTo: "To",
      emailCc: "CC",
      emailCcOptional: "Optional",
      emailSubject: "Subject",
      emailMessage: "Message",
      emailCancel: "Cancel",
      emailSend: "Send email",
      emailSending: "Sending…",
      emailSent: "Report email sent successfully.",
      emailFailed: "Could not send the report email.",
      emailFallback: "PDF downloaded. Your mail app opened — attach the downloaded report file and send.",
      emailDefaultSubject: "HAECHIE Sensor Monitoring Report",
      emailGreeting: "Hello,",
      emailBodyIntro: "Please find the sensor monitoring report below.",
      emailReportId: "Report ID",
      emailPeriod: "Period",
      emailBodyAttach: "The report is attached as a PDF (or download it if using your mail client).",
      emailClosing: "Regards,\nHAECHIE Monitoring",
      allFloors: "All floors",
      includeSections: "Include in report",
      selectAll: "Show all",
      selectNone: "Hide all",
      compareWith: "Compare with",
      previousWeek: "Previous week",
      previousDay: "Previous day",
      tabDashboard: "Report Dashboard",
      floorsTitle: "Floors",
      floorsHint: "Choose which floors to include in charts and tables.",
      tabComparison: "Week Comparison",
      tabAdmin: "Admin Log",
      executiveSummary: "Executive Summary",
      comparisonHint: "Compare sensor metrics and administrator inspection/action records between the previous week and the current week.",
      saveWeeklySnapshot: "Save This Week's Snapshot",
      saveWeeklySnapshotHint: "Save current week's sensor KPI totals to the database. Next week, this becomes the previous-week baseline for comparison.",
      savingSnapshot: "Saving snapshot…",
      snapshotSaved: "Weekly snapshot saved successfully.",
      snapshotSaveFailed: "Could not save weekly snapshot.",
      addInspection: "Log Inspection Result",
      addAction: "Log Corrective Action",
      floor: "Floor / Area",
      inspector: "Inspector",
      notes: "Notes",
      sensorId: "Sensor ID",
      event: "Event",
      actionTaken: "Action taken",
      saveInspection: "Save Inspection",
      saveAction: "Save Action",
      sections: {
        kpiMetrics: "KPI Metrics",
        executiveSummary: "Executive Summary",
        statusDistribution: "Status Distribution",
        trendOverview: "Trend Overview",
        alertSummary: "Alert Summary",
        floorSummary: "Floor-wise Summary",
        temperatureAlerts: "Temperature Alerts",
        arcRiskAlerts: "ARC Risk Alerts",
        hourlyStatistics: "Hourly Statistics",
        inspectionResults: "Inspection Results",
        correctiveActions: "Corrective Actions",
        maintenanceSummary: "Maintenance Summary",
        weeklyComparison: "Weekly Comparison",
        eventTimeline: "Event Timeline",
        aiRecommendations: "AI Recommendations"
      }
    },
    login: {
      errorBothFields: "Please enter both username and password",
      errorInvalid: "Invalid username or password",
      errorFailed: "Login failed. Please try again.",
      tempMonitoring: "Temperature Monitoring",
      irSensor: "IR Sensor",
      envSensor: "Environment Sensor",
      brandSubtitle: "Smart Sensor Monitoring Platform",
      welcomeBack: "Welcome Back",
      signInSubtitle: "Sign in to access your dashboard",
      username: "Username",
      password: "Password",
      signingIn: "Signing in...",
      signIn: "Sign In",
      signOut: "Sign out",
      loginFooter: "Secure access to HAECHIE monitoring system"
    },
    ai: {
      pageTitle: "ARC Guard AI",
      headerMessage:
        "AI-based predictive detection identifies arc faults up to 5 seconds in advance.",
      beta: "Beta",
      streamLive: "Inference stream live",
      streamConnecting: "Connecting…",
      streamOffline: "Stream offline",
      classifierOutput: "Classifier output",
      arcProbability: "ARC probability",
      stabilityIndex: "Stability index",
      edgeLink: "Edge link",
      riskMeter: "ARC risk index",
      systemStable: "Within safe envelope",
      systemWarning: "Elevated — review stream",
      edgeContext: "Edge context",
      inferenceStream: "Inference stream",
      operationalPlaybook: "Operational playbook"
    }
  },
  ko: {
    sidebar: {
      dashboard: "대시보드",
      installationMap: "설치 정보",
      environ: "환경",
      monitoring: "모니터링",
      adc: "ADC",
      logs: "로그",
      report: "보고서",
      settings: "설정",
      aiModel: "AI 모델 (베타)"
    },
    common: {
      connecting: "연결 중...",
      checking: "확인 중...",
      normal: "정상",
      enter: "입력",
      cancel: "취소",
      connect: "연결"
    },
    connection: {
      connecting: "실시간 데이터 연결 중…",
      reconnecting: "실시간 데이터 재연결 중…",
      offline: "오프라인 상태입니다",
      recovered: "실시간 데이터 복구됨",
      lastUpdate: "마지막 업데이트"
    },
    settings: {
      title: "시스템 구성",
      subtitle: "프로토콜, 사운드, 언어, 화면 및 접근 설정을 관리합니다.",
      language: "언어",
      appearance: "화면",
      darkMode: "다크 모드",
      lightMode: "라이트 모드",
      themeChanged: "테마가 업데이트되었습니다.",
      tabs: { general: "일반", wifi: "WiFi", tcpip: "TCP/IP", modbus: "Modbus", installation: "설치 정보" },
      activeProtocol: "활성 프로토콜",
      configureActivate: "구성 및 프로토콜 활성화",
      activating: "활성화 중...",
      currentProtocol: "현재 프로토콜",
      noneSelected: "선택 없음",
      active: "(활성)",
      inactive: "(비활성)",
      alertSound: "알림 사운드",
      saveSound: "사운드 저장",
      saving: "저장 중...",
      applicationAccess: "애플리케이션 접근",
      locating: "위치 확인 중...",
      copied: "복사됨!",
      selectProtocolFirst: "먼저 프로토콜을 선택하세요.",
      saveConfig: "구성 저장",
      saveConfigSuccess: "WiFi 구성이 저장되었습니다.",
      soundSaved: "사운드 설정이 저장되었습니다.",
      languageChanged: "언어가 변경되었습니다.",
      manual: "수동",
      autoDhcp: "자동 (DHCP)",
      ipAddress: "IP 주소",
      systemIp: "시스템 IP",
      activatingDialogTitle: "프로토콜 활성화",
      activatingDialogText: "연결 및 구성 적용 중...",
      companyLogo: "회사 로고",
      companyLogoHint: "보고서 페이지 및 PDF 내보내기에 표시할 회사 로고를 업로드하세요.",
      companyLogoUpload: "로고 업로드",
      companyLogoRemove: "로고 제거",
      companyLogoUploading: "업로드 중…",
      companyLogoUploaded: "회사 로고가 저장되었습니다. 프로그램을 다시 실행해도 유지됩니다.",
      companyLogoRemoved: "회사 로고가 제거되었습니다.",
      companyLogoFailed: "회사 로고를 업데이트할 수 없습니다.",
      companyLogoFormats: "PNG, JPG, GIF, BMP 또는 WEBP · 권장 400×120 px"
    },
    installation: {
      title: "설치된 H-LINK 및 센서 정보",
      subtitle: "설치 위치 및 관리 ID를 입력합니다. 데이터는 PC에 저장되며 대시보드와 모니터링에 표시됩니다.",
      subtitleShort: "H-LINK·센서별 설치 정보 입력 — 로컬 저장, 대시보드·모니터링에 표시.",
      writeFunctionNote: "쓰기 기능: 초기 설치 시 각 H-LINK 및 센서 장치의 설치 정보를 입력·저장합니다. 설치 위치, 좌표, 맵/마크 정보, 장치 관리 번호, 기타 식별 정보(비고)를 포함합니다.",
      installationLocation: "설치 위치",
      managementNumber: "장치 관리 번호",
      colMgmtNumber: "관리 번호",
      mgmtPlaceholder: "예: MGT-001",
      addHlinkDialogTitle: "H-LINK 추가",
      addHlinkDialogDesc: "장치 라벨의 H-LINK 시리얼 번호를 입력하세요. 장치가 온라인이 되기 전에도 센서와 설치 정보를 추가할 수 있습니다.",
      addHlinkSerialPlaceholder: "H-LINK S/N 입력",
      serialRequired: "시리얼 번호가 필요합니다.",
      hlinkExists: "이 H-LINK 시리얼 번호가 이미 목록에 있습니다.",
      hlinkAdded: "H-LINK가 추가되었습니다. 센서를 추가한 후 모든 정보 저장을 클릭하세요.",
      sensorAdded: "센서 {id}이(가) 추가되었습니다.",
      sensorLimit: "H-LINK당 최대 12개 센서입니다.",
      removeHlink: "H-LINK 제거",
      clearHlink: "모든 필드 지우기",
      removeHlinkConfirm: "설치 정보에서 H-LINK {serial}을(를) 제거할까요? 이 작업은 되돌릴 수 없습니다.",
      removeHlinkSuccess: "설치 정보에서 H-LINK가 제거되었습니다.",
      cannotRemoveLive: "연결된 H-LINK는 제거할 수 없습니다. 먼저 연결을 해제하거나 필드를 지우세요.",
      noDevices: "H-LINK 장치가 감지되지 않았습니다. 장치를 연결한 후 다시 시도하세요.",
      connected: "연결됨",
      disconnected: "연결 안 됨",
      serialReadOnly: "H-LINK S/N",
      serialNotEditable: "시리얼 번호는 변경할 수 없습니다",
      hlinkId: "H-LINK S/N (읽기 전용)",
      hlinkWriteId: "H-LINK ID (입력)",
      installInfo: "설치 정보",
      installDate: "설치 일자",
      remarks: "비고",
      sensorTableTitle: "H-LINK #{index}에 연결된 센서",
      colNo: "번호",
      colSensorId: "센서 ID",
      colType: "센서 유형",
      colInstallInfo: "설치 정보",
      colRemarks: "비고",
      colStatus: "상태",
      footerNote: "H-LINK 하나당 최대 12개의 센서를 연결할 수 있습니다. 여기에 입력한 정보는 대시보드와 모니터링 화면에 표시됩니다.",
      footerCompact: "센서는 연결된 H-LINK에서 자동 감지됩니다. H-LINK당 최대 12개. 대시보드·모니터링에 표시. S/N은 읽기 전용.",
      connectedTotal: "연결된 센서 총계: {count}",
      sensorsConnected: "센서 (연결: {connected} / 최대: {max})",
      writeAll: "모든 정보 저장",
      saving: "저장 중…",
      saveSuccess: "설치 정보가 저장되었습니다.",
      saveFailed: "설치 정보를 저장할 수 없습니다.",
      locationLabel: "위치",
      hlinkSection: "H-LINK 설치 상세",
      locationPlaceholder: "예: 로비, A라인",
      loading: "설치 정보 불러오는 중…",
      statHlinks: "H-LINK 장치",
      statSensors: "연결된 센서",
      noDevicesTitle: "장치가 감지되지 않음",
      maxChars: "(최대 {max}자)",
      remarksPlaceholder: "기타 식별 정보 / 메모",
      editHlink: "H-LINK 정보 편집",
      delete: "삭제",
      sensorCount: "센서: {count}",
      addSensor: "센서 추가",
      addHlink: "H-LINK 추가",
      addHlinkHint: "초기 설치를 위해 시리얼 번호로 H-LINK 추가",
      colAction: "작업",
      editRow: "편집",
      clearRow: "필드 지우기",
      noteTitle: "참고",
      noteDashboard: "여기에 입력한 정보는 대시보드와 모니터링 메뉴에 표시됩니다.",
      noteSerial: "시리얼 번호(S/N)는 읽기 전용이며 변경할 수 없습니다.",
      positionCoords: "위치 좌표",
      mapMarkInfo: "맵 / 마크 정보",
      mapMarkPlaceholder: "예: Zone A, Mark-3",
      mapMarkAutoPlaceholder: "좌표에서 자동 생성",
      mapMarkAutoHint: "맵 마크는 위치 좌표(x, y)에서 자동으로 생성됩니다.",
      uploadMap: "평면도 업로드",
      mapUploaded: "평면도가 업로드되었습니다.",
      noMapYet: "업로드된 맵 없음",
      draft: "임시",
      collapseAll: "모두 접기",
      waitingSensors: "연결된 H-LINK에서 센서를 기다리는 중…",
      snMismatchHint: "이 S/N에 저장된 설치 정보가 없습니다. 설정 → 설치 정보에서 입력하세요.",
      mapUploadHint: "이 H-LINK용 평면도 저장 (선택).",
      envSensorsHint: "환경 센서 정보는 모니터링 카드에만 표시되며 설치 맵에는 표시되지 않습니다.",
    },
    installMap: {
      title: "설치 맵 보기",
      subtitle: "저장된 설치 정보에 따른 H-LINK 평면도 및 센서 위치입니다.",
      subtitleWorld: "세계 지도에서 H-LINK가 설치된 위치를 표시하세요.",
      subtitle3d: "설치 설정 기반 3D 플로어 뷰와 실시간 센서 마커입니다.",
      viewWorld: "세계 지도",
      view2d: "2D",
      view3d: "3D",
      worldHint: "목록에서 H-LINK를 선택한 뒤 세계 지도를 클릭해 위치를 지정하세요.",
      worldHintPlace: "지도를 클릭해 S/N {serial} 설치 위치를 지정하세요. 핀을 드래그해 이동할 수 있습니다.",
      worldCoords: "세계 좌표",
      clearPin: "핀 제거",
      worldPlaced: "세계 지도에 표시됨",
      worldNotPlaced: "아직 표시되지 않음",
      controls3d: "드래그: 회전 · 스크롤: 확대/축소 · 마커 클릭: 센서 선택",
      noDevices: "연결된 H-LINK가 없습니다. 먼저 설정에서 설치 정보를 입력하세요.",
      noMapImage: "평면도 이미지 없음",
      uploadInSettings: "설정 → 설치 정보에서 이 H-LINK의 평면도를 업로드하세요.",
      sensorDetails: "선택된 센서",
      selectSensorHint: "맵 또는 아래 목록에서 센서를 클릭하면 상세 정보가 표시됩니다.",
      clearSelection: "선택 해제",
      sensorsByHlink: "H-LINK별 센서",
      hlinkSensors: "센서 {count}개",
      allSensors: "전체 센서",
      noSensors: "이 H-LINK에 센서가 없습니다.",
      noMark: "마크 정보 없음",
      fieldId: "센서 ID",
      fieldLocation: "설치 위치",
      fieldCoords: "좌표",
      fieldMark: "맵 / 마크 정보",
      fieldMgmt: "관리 번호",
      fieldStatus: "상태",
      fieldTemp: "온도"
    },
    arc: {
      emergency: "비상",
      critical: "위험",
      warning: "경고",
      eWarning: "조기 경고",
      noRisk: "위험 없음",
      normal: "정상",
      severe: "심각",
      danger: "위험",
      caution: "주의"
    },
    dashboard: {
      pageTitle: "e-HAECHIE 센서 실시간 모니터링",
      arcFault: "아크 결함",
      temperature: "온도",
      max: "최대",
      avg: "평균",
      min: "최소",
      totalOperationStatus: "전체 운영 상태",
      lineOperation: "라인 운영",
      sensorOperation: "센서 운영",
      anomalyDetection: "이상 감지",
      check: "확인",
      sensorDeviceStatus: "센서 장치 상태",
      enterDeviceId: "장치 ID 입력",
      deviceId: "장치_ID",
      lora: "LoRA",
      status: "상태",
      noDataAvailable: "데이터 없음",
      sensingStatus: "감지 상태",
      noRisk: "위험 없음",
      emergency: "비상",
      critical: "위험",
      warning: "경고",
      eventLogs: "이벤트 로그",
      time: "시간",
      arcLevel: "아크 레벨",
      sensorStatus: "센서 상태",
      waitingDevices: "장치 데이터 대기 중...",
      resetWarnings: "경고 초기화",
      anomalyInsight: "이상 인사이트",
      thresholdConfig: "임계값 구성"
    },
    monitoring: {
      pageTitle: "e-HAECHIE 센서 실시간 모니터링",
      arcFault: "아크 결함",
      arcFaultLabel: "아크 결함",
      normalLabel: "정상",
      temperature: "온도",
      deviceSerial: "장치 시리얼",
      irTempSensors: "IR 및 온도 센서",
      envSensors: "환경 센서",
      noSensors: "연결된 센서 없음.",
      resetWarnings: "경고 초기화",
      anomalyInsight: "이상 인사이트",
      thresholdConfig: "임계값 구성"
    },
    adc: {
      pageTitle: "e-HAECHIE 센서 실시간 모니터링",
      downloadReport: "보고서 다운로드",
      waitingDevices: "활성 H-Link 장치 없음",
      arcLevel: "아크 레벨",
      maxValue: "최대값",
      waitingSensorData: "센서 데이터 대기 중...",
      selectDeviceTab: "활성 장치 탭을 선택하거나 연결을 확인하세요.",
      downloading: "다운로드 중...",
      download: "다운로드"
    },
    environ: {
      pageTitle: "환경 대시보드",
      charts: "차트",
      table: "테이블",
      noDevices: "연결된 장치 없음",
      waitingSensors: "센서 데이터 대기 중...",
      viewingAll: "모든 센서 보기",
      from: "시작",
      to: "종료",
      loadingData: "데이터 로딩 중...",
      sensorId: "센서 ID",
      temp: "온도 (°C)",
      humidity: "습도 (%)",
      pressure: "기압 (hPa)",
      eco2: "eCO2 (ppm)",
      iaq: "IAQ",
      pm1: "PM1.0",
      pm25: "PM2.5",
      pm10: "PM10",
      noData: "선택한 기간에 데이터 없음"
    },
    logs: {
      title: "이벤트 로그",
      arcSection: "아크 이벤트",
      tempSection: "온도 이벤트",
      no: "번호",
      date: "날짜",
      time: "시간",
      sensorId: "센서 ID",
      event: "이벤트",
      arcLevel: "아크 레벨",
      detail: "상세",
      tempDetail: "온도 / 임계값",
      action: "작업",
      arcDetection: "아크 감지",
      tempOverheat: "온도 과열",
      noEvents: "이벤트가 없습니다",
      viewGraph: "상세",
      cycleGraphTitle: "온도 사이클",
      temperature: "온도",
      threshold: "임계값",
      tempCol: "Temp",
      thrCol: "Thr",
      cycleActive: "임계값 초과 중 — 업데이트 중…",
      cycleEnded: "정상 복귀 시각",
      cycleLoading: "사이클 불러오는 중…",
      cycleFailed: "사이클 데이터를 불러올 수 없습니다.",
      cycleMissing: "이 이벤트에 대한 사이클이 아직 없습니다. 백엔드 업데이트 이후의 새 과열 이벤트부터 기록됩니다.",
      cycleNoSamples: "이 사이클에 샘플이 없습니다.",
      delete: "삭제",
      sensor: "센서",
      arcData: "아크 데이터",
      confirmDeletion: "삭제 확인",
      confirmDeleteMessage: "다음 센서의 로그를 삭제하시겠습니까",
      at: "시간",
      logDeleted: "로그가 삭제되었습니다",
      deleteFailed: "로그 삭제 실패",
      wsError: "WebSocket 연결 오류."
    },
    login: {
      errorBothFields: "사용자 이름과 비밀번호를 입력하세요",
      errorInvalid: "잘못된 사용자 이름 또는 비밀번호",
      errorFailed: "로그인 실패. 다시 시도하세요.",
      tempMonitoring: "온도 모니터링",
      irSensor: "IR 센서",
      envSensor: "환경 센서",
      brandSubtitle: "스마트 센서 모니터링 플랫폼",
      welcomeBack: "다시 오신 것을 환영합니다",
      signInSubtitle: "대시보드에 접근하려면 로그인하세요",
      username: "사용자 이름",
      password: "비밀번호",
      signingIn: "로그인 중...",
      signIn: "로그인",
      loginFooter: "HAECHIE 모니터링 시스템 보안 접근"
    },
    ai: {
      pageTitle: "ARC Guard AI",
      headerMessage: "AI 기반 예측 감지는 아크 결함을 최대 5초 전에 식별합니다.",
      beta: "베타",
      streamLive: "추론 스트림 연결됨",
      streamConnecting: "연결 중…",
      streamOffline: "스트림 오프라인",
      classifierOutput: "분류기 출력",
      arcProbability: "아크 확률",
      stabilityIndex: "안정성 지수",
      edgeLink: "엣지 링크",
      riskMeter: "아크 위험 지수",
      systemStable: "안전 범위 내",
      systemWarning: "상승 — 스트림 확인",
      edgeContext: "엣지 컨텍스트",
      inferenceStream: "추론 스트림",
      operationalPlaybook: "운영 플레이북"
    }
  },
  ja: {
    sidebar: {
      dashboard: "ダッシュボード",
      installationMap: "設置情報",
      environ: "環境",
      monitoring: "モニタリング",
      adc: "ADC",
      logs: "ログ",
      report: "レポート",
      settings: "設定",
      aiModel: "AIモデル (ベータ)"
    },
    common: {
      connecting: "接続中...",
      checking: "確認中...",
      normal: "正常",
      enter: "入力",
      cancel: "キャンセル",
      connect: "接続"
    },
    connection: {
      connecting: "ライブデータに接続中…",
      reconnecting: "ライブデータに再接続中…",
      offline: "オフラインです",
      recovered: "ライブデータが復旧しました",
      lastUpdate: "最終更新"
    },
    settings: {
      title: "システム構成",
      subtitle: "プロトコル、サウンド、言語、表示、アクセス設定を管理します。",
      language: "言語",
      appearance: "表示",
      darkMode: "ダークモード",
      lightMode: "ライトモード",
      themeChanged: "テーマを更新しました。",
      tabs: { general: "一般", wifi: "WiFi", tcpip: "TCP/IP", modbus: "Modbus", installation: "設置情報" },
      activeProtocol: "アクティブプロトコル",
      configureActivate: "構成してプロトコルを有効化",
      activating: "有効化中...",
      currentProtocol: "現在のプロトコル",
      noneSelected: "未選択",
      active: "(有効)",
      inactive: "(無効)",
      alertSound: "アラート音",
      saveSound: "サウンドを保存",
      saving: "保存中...",
      applicationAccess: "アプリケーションアクセス",
      locating: "検索中...",
      copied: "コピーしました!",
      selectProtocolFirst: "先にプロトコルを選択してください。",
      saveConfig: "構成を保存",
      saveConfigSuccess: "WiFi構成を保存しました。",
      soundSaved: "サウンド設定を保存しました。",
      languageChanged: "言語を更新しました。",
      manual: "手動",
      autoDhcp: "自動 (DHCP)",
      ipAddress: "IPアドレス",
      systemIp: "システムIP",
      activatingDialogTitle: "プロトコルを有効化",
      activatingDialogText: "接続して構成を適用しています...",
      companyLogo: "会社ロゴ",
      companyLogoHint: "レポートページとPDFエクスポートに表示する会社ロゴをアップロードします。",
      companyLogoUpload: "ロゴをアップロード",
      companyLogoRemove: "ロゴを削除",
      companyLogoUploading: "アップロード中…",
      companyLogoUploaded: "会社ロゴを保存しました。次回起動時も保持されます。",
      companyLogoRemoved: "会社ロゴを削除しました。",
      companyLogoFailed: "会社ロゴを更新できませんでした。",
      companyLogoFormats: "PNG、JPG、GIF、BMP、または WEBP · 推奨 400×120 px"
    },
    arc: {
      emergency: "緊急",
      critical: "重大",
      warning: "警告",
      eWarning: "早期警告",
      noRisk: "リスクなし",
      normal: "正常",
      severe: "深刻",
      danger: "危険",
      caution: "注意"
    },
    dashboard: {
      pageTitle: "e-HAECHIE センサー リアルタイム監視",
      arcFault: "アーク障害",
      temperature: "温度",
      max: "最大",
      avg: "平均",
      min: "最小",
      totalOperationStatus: "総合運転状態",
      lineOperation: "ライン運転",
      sensorOperation: "センサー運転",
      anomalyDetection: "異常検知",
      check: "確認",
      sensorDeviceStatus: "センサー装置状態",
      enterDeviceId: "装置IDを入力",
      deviceId: "装置_ID",
      lora: "LoRA",
      status: "状態",
      noDataAvailable: "データなし",
      sensingStatus: "検知状態",
      noRisk: "リスクなし",
      emergency: "緊急",
      critical: "重大",
      warning: "警告",
      eventLogs: "イベントログ",
      time: "時間",
      arcLevel: "アークレベル",
      sensorStatus: "センサー状態",
      waitingDevices: "装置データ待機中...",
      resetWarnings: "警告リセット",
      anomalyInsight: "異常インサイト",
      thresholdConfig: "閾値設定"
    },
    monitoring: {
      pageTitle: "e-HAECHIE センサー リアルタイム監視",
      arcFault: "アーク障害",
      arcFaultLabel: "アーク障害",
      normalLabel: "正常",
      temperature: "温度",
      deviceSerial: "装置シリアル",
      irTempSensors: "IR・温度センサー",
      envSensors: "環境センサー",
      noSensors: "接続されたセンサーがありません。",
      resetWarnings: "警告リセット",
      anomalyInsight: "異常インサイト",
      thresholdConfig: "閾値設定"
    },
    adc: {
      pageTitle: "e-HAECHIE センサー リアルタイム監視",
      downloadReport: "レポートをダウンロード",
      waitingDevices: "アクティブなH-Link装置なし",
      arcLevel: "アークレベル",
      maxValue: "最大値",
      waitingSensorData: "センサーデータ待機中...",
      selectDeviceTab: "アクティブな装置タブを選択するか接続を確認してください。",
      downloading: "ダウンロード中...",
      download: "ダウンロード"
    },
    environ: {
      pageTitle: "環境ダッシュボード",
      charts: "チャート",
      table: "テーブル",
      noDevices: "接続された装置なし",
      waitingSensors: "センサーデータ待機中...",
      viewingAll: "すべてのセンサーを表示",
      from: "開始",
      to: "終了",
      loadingData: "データ読み込み中...",
      sensorId: "センサーID",
      temp: "温度 (°C)",
      humidity: "湿度 (%)",
      pressure: "気圧 (hPa)",
      eco2: "eCO2 (ppm)",
      iaq: "IAQ",
      pm1: "PM1.0",
      pm25: "PM2.5",
      pm10: "PM10",
      noData: "選択範囲にデータなし"
    },
    logs: {
      title: "イベントログ",
      arcSection: "アークイベント",
      tempSection: "温度イベント",
      no: "番号",
      date: "日付",
      time: "時間",
      sensorId: "センサーID",
      event: "イベント",
      arcLevel: "アークレベル",
      detail: "詳細",
      tempDetail: "温度 / 閾値",
      action: "操作",
      arcDetection: "アーク検出",
      tempOverheat: "温度過熱",
      noEvents: "イベントはまだありません",
      viewGraph: "詳細",
      cycleGraphTitle: "温度サイクル",
      temperature: "温度",
      threshold: "閾値",
      tempCol: "Temp",
      thrCol: "Thr",
      cycleActive: "閾値超過中 — 更新中…",
      cycleEnded: "正常復帰時刻",
      cycleLoading: "サイクル読込中…",
      cycleFailed: "サイクルデータを読み込めませんでした。",
      cycleMissing: "このイベントのサイクルはまだありません。バックエンド更新後の新規過熱イベントから記録されます。",
      cycleNoSamples: "このサイクルにサンプルがありません。",
      delete: "削除",
      sensor: "センサー",
      arcData: "アークデータ",
      confirmDeletion: "削除の確認",
      confirmDeleteMessage: "次のセンサーのログを削除しますか",
      at: "時刻",
      logDeleted: "ログを削除しました",
      deleteFailed: "ログの削除に失敗しました",
      wsError: "WebSocket接続エラー。"
    },
    login: {
      errorBothFields: "ユーザー名とパスワードを入力してください",
      errorInvalid: "ユーザー名またはパスワードが無効です",
      errorFailed: "ログインに失敗しました。再試行してください。",
      tempMonitoring: "温度監視",
      irSensor: "IRセンサー",
      envSensor: "環境センサー",
      brandSubtitle: "スマートセンサー監視プラットフォーム",
      welcomeBack: "おかえりなさい",
      signInSubtitle: "ダッシュボードにアクセスするにはサインインしてください",
      username: "ユーザー名",
      password: "パスワード",
      signingIn: "サインイン中...",
      signIn: "サインイン",
      loginFooter: "HAECHIE監視システムへの安全なアクセス"
    },
    ai: {
      pageTitle: "ARC Guard AI",
      headerMessage: "AIベースの予測検出は、アーク障害を最大5秒前に識別します。",
      beta: "ベータ",
      streamLive: "推論ストリーム接続中",
      streamConnecting: "接続中…",
      streamOffline: "ストリームオフライン",
      classifierOutput: "分類器出力",
      arcProbability: "アーク確率",
      stabilityIndex: "安定性指数",
      edgeLink: "エッジリンク",
      riskMeter: "アークリスク指数",
      systemStable: "安全範囲内",
      systemWarning: "上昇 — ストリームを確認",
      edgeContext: "エッジコンテキスト",
      inferenceStream: "推論ストリーム",
      operationalPlaybook: "運用プレイブック"
    }
  },
  zh: {
    sidebar: {
      dashboard: "仪表板",
      installationMap: "安装信息",
      environ: "环境",
      monitoring: "监控",
      adc: "ADC",
      logs: "日志",
      report: "报告",
      settings: "设置",
      aiModel: "AI 模型 (测试版)"
    },
    common: {
      connecting: "连接中...",
      checking: "检查中...",
      normal: "正常",
      enter: "输入",
      cancel: "取消",
      connect: "连接"
    },
    connection: {
      connecting: "正在连接实时数据…",
      reconnecting: "正在重新连接实时数据…",
      offline: "您已离线",
      recovered: "实时数据已恢复",
      lastUpdate: "最后更新"
    },
    settings: {
      title: "系统配置",
      subtitle: "管理协议、声音、语言、外观及访问设置。",
      language: "语言",
      appearance: "外观",
      darkMode: "深色模式",
      lightMode: "浅色模式",
      themeChanged: "主题已更新。",
      tabs: { general: "常规", wifi: "WiFi", tcpip: "TCP/IP", modbus: "Modbus", installation: "安装信息" },
      activeProtocol: "活动协议",
      configureActivate: "配置并激活协议",
      activating: "激活中...",
      currentProtocol: "当前协议",
      noneSelected: "未选择",
      active: "(已激活)",
      inactive: "(未激活)",
      alertSound: "警报声音",
      saveSound: "保存声音",
      saving: "保存中...",
      applicationAccess: "应用访问",
      locating: "定位中...",
      copied: "已复制!",
      selectProtocolFirst: "请先选择协议。",
      saveConfig: "保存配置",
      saveConfigSuccess: "WiFi 配置已保存。",
      soundSaved: "声音偏好已保存。",
      languageChanged: "语言已更新。",
      manual: "手动",
      autoDhcp: "自动 (DHCP)",
      ipAddress: "IP 地址",
      systemIp: "系统 IP",
      activatingDialogTitle: "正在激活协议",
      activatingDialogText: "正在连接并应用配置...",
      companyLogo: "公司标志",
      companyLogoHint: "上传公司标志，将显示在报告页面和 PDF 导出中。",
      companyLogoUpload: "上传标志",
      companyLogoRemove: "移除标志",
      companyLogoUploading: "上传中…",
      companyLogoUploaded: "公司标志已保存。下次运行程序时仍会保留。",
      companyLogoRemoved: "公司标志已移除。",
      companyLogoFailed: "无法更新公司标志。",
      companyLogoFormats: "PNG、JPG、GIF、BMP 或 WEBP · 建议 400×120 px"
    },
    arc: {
      emergency: "紧急",
      critical: "严重",
      warning: "警告",
      eWarning: "早期警告",
      noRisk: "无风险",
      normal: "正常",
      severe: "严重",
      danger: "危险",
      caution: "注意"
    },
    dashboard: {
      pageTitle: "e-HAECHIE 传感器实时监控",
      arcFault: "电弧故障",
      temperature: "温度",
      max: "最大",
      avg: "平均",
      min: "最小",
      totalOperationStatus: "总体运行状态",
      lineOperation: "线路运行",
      sensorOperation: "传感器运行",
      anomalyDetection: "异常检测",
      check: "检查",
      sensorDeviceStatus: "传感器设备状态",
      enterDeviceId: "输入设备 ID",
      deviceId: "设备_ID",
      lora: "LoRA",
      status: "状态",
      noDataAvailable: "无数据",
      sensingStatus: "检测状态",
      noRisk: "无风险",
      emergency: "紧急",
      critical: "严重",
      warning: "警告",
      eventLogs: "事件日志",
      time: "时间",
      arcLevel: "电弧等级",
      sensorStatus: "传感器状态",
      waitingDevices: "等待设备数据...",
      resetWarnings: "重置警告",
      anomalyInsight: "异常洞察",
      thresholdConfig: "阈值配置"
    },
    monitoring: {
      pageTitle: "e-HAECHIE 传感器实时监控",
      arcFault: "电弧故障",
      arcFaultLabel: "电弧故障",
      normalLabel: "正常",
      temperature: "温度",
      deviceSerial: "设备序列号",
      irTempSensors: "红外和温度传感器",
      envSensors: "环境传感器",
      noSensors: "未连接传感器。",
      resetWarnings: "重置警告",
      anomalyInsight: "异常洞察",
      thresholdConfig: "阈值配置"
    },
    adc: {
      pageTitle: "e-HAECHIE 传感器实时监控",
      downloadReport: "下载报告",
      waitingDevices: "无活动 H-Link 设备",
      arcLevel: "电弧等级",
      maxValue: "最大值",
      waitingSensorData: "等待传感器数据...",
      selectDeviceTab: "选择活动设备标签或检查连接。",
      downloading: "下载中...",
      download: "下载"
    },
    environ: {
      pageTitle: "环境仪表板",
      charts: "图表",
      table: "表格",
      noDevices: "未连接设备",
      waitingSensors: "等待传感器数据...",
      viewingAll: "查看所有传感器",
      from: "从",
      to: "到",
      loadingData: "加载数据中...",
      sensorId: "传感器 ID",
      temp: "温度 (°C)",
      humidity: "湿度 (%)",
      pressure: "气压 (hPa)",
      eco2: "eCO2 (ppm)",
      iaq: "IAQ",
      pm1: "PM1.0",
      pm25: "PM2.5",
      pm10: "PM10",
      noData: "所选范围无数据"
    },
    logs: {
      title: "事件日志",
      arcSection: "电弧事件",
      tempSection: "温度事件",
      no: "编号",
      date: "日期",
      time: "时间",
      sensorId: "传感器 ID",
      event: "事件",
      arcLevel: "电弧等级",
      detail: "详情",
      tempDetail: "温度 / 阈值",
      action: "操作",
      arcDetection: "电弧检测",
      tempOverheat: "温度过热",
      noEvents: "暂无事件",
      viewGraph: "详情",
      cycleGraphTitle: "温度周期",
      temperature: "温度",
      threshold: "阈值",
      tempCol: "Temp",
      thrCol: "Thr",
      cycleActive: "仍超阈值 — 更新中…",
      cycleEnded: "恢复正常时间",
      cycleLoading: "正在加载周期…",
      cycleFailed: "无法加载周期数据。",
      cycleMissing: "该事件尚无周期数据。后端更新后的新过热事件才会记录。",
      cycleNoSamples: "该周期没有采样数据。",
      delete: "删除",
      sensor: "传感器",
      arcData: "电弧数据",
      confirmDeletion: "确认删除",
      confirmDeleteMessage: "确定要删除以下传感器的日志吗",
      at: "于",
      logDeleted: "日志已删除",
      deleteFailed: "删除日志失败",
      wsError: "WebSocket 连接错误。"
    },
    login: {
      errorBothFields: "请输入用户名和密码",
      errorInvalid: "用户名或密码无效",
      errorFailed: "登录失败，请重试。",
      tempMonitoring: "温度监控",
      irSensor: "红外传感器",
      envSensor: "环境传感器",
      brandSubtitle: "智能传感器监控平台",
      welcomeBack: "欢迎回来",
      signInSubtitle: "登录以访问您的仪表板",
      username: "用户名",
      password: "密码",
      signingIn: "登录中...",
      signIn: "登录",
      loginFooter: "安全访问 HAECHIE 监控系统"
    },
    ai: {
      pageTitle: "ARC Guard AI",
      headerMessage: "基于 AI 的预测检测可在电弧故障发生前最多 5 秒识别。",
      beta: "测试版",
      streamLive: "推理流已连接",
      streamConnecting: "连接中…",
      streamOffline: "流离线",
      classifierOutput: "分类器输出",
      arcProbability: "电弧概率",
      stabilityIndex: "稳定性指数",
      edgeLink: "边缘链接",
      riskMeter: "电弧风险指数",
      systemStable: "处于安全范围",
      systemWarning: "升高 — 请检查流",
      edgeContext: "边缘上下文",
      inferenceStream: "推理流",
      operationalPlaybook: "操作手册"
    }
  },
  fr: {
    sidebar: {
      dashboard: "Tableau de bord",
      installationMap: "Infos d'installation",
      environ: "Environnement",
      monitoring: "Surveillance",
      adc: "ADC",
      logs: "Journaux",
      report: "Rapport",
      settings: "Paramètres",
      aiModel: "Modèle IA (Bêta)"
    },
    common: {
      connecting: "Connexion...",
      checking: "Vérification...",
      normal: "Normal",
      enter: "Entrer",
      cancel: "Annuler",
      connect: "Connecter"
    },
    connection: {
      connecting: "Connexion aux données en direct…",
      reconnecting: "Reconnexion aux données en direct…",
      offline: "Vous êtes hors ligne",
      recovered: "Données en direct rétablies",
      lastUpdate: "Dernière mise à jour"
    },
    settings: {
      title: "Configuration système",
      subtitle: "Gérez le protocole, le son, la langue, l'apparence et l'accès.",
      language: "Langue",
      appearance: "Apparence",
      darkMode: "Mode sombre",
      lightMode: "Mode clair",
      themeChanged: "Thème mis à jour.",
      tabs: { general: "Général", wifi: "WiFi", tcpip: "TCP/IP", modbus: "Modbus", installation: "Infos d'installation" },
      activeProtocol: "Protocole actif",
      configureActivate: "Configurer et activer le protocole",
      activating: "Activation...",
      currentProtocol: "Protocole actuel",
      noneSelected: "Aucune sélection",
      active: "(Actif)",
      inactive: "(Inactif)",
      alertSound: "Son d'alerte",
      saveSound: "Enregistrer le son",
      saving: "Enregistrement...",
      applicationAccess: "Accès à l'application",
      locating: "Localisation...",
      copied: "Copié !",
      selectProtocolFirst: "Veuillez d'abord sélectionner un protocole.",
      saveConfig: "Enregistrer la configuration",
      saveConfigSuccess: "Configuration WiFi enregistrée.",
      soundSaved: "Préférence sonore enregistrée.",
      languageChanged: "Langue mise à jour.",
      manual: "Manuel",
      autoDhcp: "Auto (DHCP)",
      ipAddress: "Adresse IP",
      systemIp: "IP système",
      activatingDialogTitle: "Activation du protocole",
      activatingDialogText: "Connexion et application de la configuration...",
      companyLogo: "Logo de l'entreprise",
      companyLogoHint: "Téléversez le logo affiché sur la page de rapport et les exports PDF.",
      companyLogoUpload: "Téléverser le logo",
      companyLogoRemove: "Supprimer le logo",
      companyLogoUploading: "Téléversement…",
      companyLogoUploaded: "Logo enregistré. Il sera conservé au prochain démarrage.",
      companyLogoRemoved: "Logo supprimé.",
      companyLogoFailed: "Impossible de mettre à jour le logo.",
      companyLogoFormats: "PNG, JPG, GIF, BMP ou WEBP · 400×120 px recommandé"
    },
    arc: {
      emergency: "URGENCE",
      critical: "CRITIQUE",
      warning: "AVERTISSEMENT",
      eWarning: "PRÉ-AVERTISSEMENT",
      noRisk: "Aucun risque",
      normal: "NORMAL",
      severe: "GRAVE",
      danger: "DANGER",
      caution: "PRUDENCE"
    },
    dashboard: {
      pageTitle: "Surveillance en temps réel e-HAECHIE",
      arcFault: "DÉFAUT D'ARC",
      temperature: "TEMPÉRATURE",
      max: "Max",
      avg: "Moy",
      min: "Min",
      totalOperationStatus: "État global de fonctionnement",
      lineOperation: "Fonctionnement ligne",
      sensorOperation: "Fonctionnement capteur",
      anomalyDetection: "Détection d'anomalie",
      check: "Vérifier",
      sensorDeviceStatus: "État des capteurs",
      enterDeviceId: "Entrer l'ID appareil",
      deviceId: "ID_appareil",
      lora: "LoRA",
      status: "Statut",
      noDataAvailable: "Aucune donnée",
      sensingStatus: "État de détection",
      noRisk: "Aucun risque",
      emergency: "Urgence",
      critical: "Critique",
      warning: "Avertissement",
      eventLogs: "Journaux d'événements",
      time: "Heure",
      arcLevel: "Niveau d'arc",
      sensorStatus: "ÉTAT DES CAPTEURS",
      waitingDevices: "En attente des données appareil...",
      resetWarnings: "Réinitialiser les alertes",
      anomalyInsight: "Aperçu anomalie",
      thresholdConfig: "Config. seuils"
    },
    monitoring: {
      pageTitle: "Surveillance en temps réel e-HAECHIE",
      arcFault: "DÉFAUT D'ARC",
      arcFaultLabel: "Défaut d'arc",
      normalLabel: "Normal",
      temperature: "TEMPÉRATURE",
      deviceSerial: "Numéro de série",
      irTempSensors: "Capteurs IR et température",
      envSensors: "Capteurs environnement",
      noSensors: "Aucun capteur connecté.",
      resetWarnings: "Réinitialiser les alertes",
      anomalyInsight: "Aperçu anomalie",
      thresholdConfig: "Config. seuils"
    },
    adc: {
      pageTitle: "Surveillance en temps réel e-HAECHIE",
      downloadReport: "Télécharger le rapport",
      waitingDevices: "Aucun appareil H-Link actif",
      arcLevel: "Niveau d'arc",
      maxValue: "Valeur max",
      waitingSensorData: "En attente des données capteur...",
      selectDeviceTab: "Sélectionnez un onglet appareil actif ou vérifiez la connexion.",
      downloading: "Téléchargement...",
      download: "Télécharger"
    },
    environ: {
      pageTitle: "Tableau de bord environnement",
      charts: "Graphiques",
      table: "Tableau",
      noDevices: "Aucun appareil connecté",
      waitingSensors: "En attente des données capteur...",
      viewingAll: "Voir tous les capteurs",
      from: "De",
      to: "À",
      loadingData: "Chargement des données...",
      sensorId: "ID capteur",
      temp: "Temp (°C)",
      humidity: "Humidité (%)",
      pressure: "Pression (hPa)",
      eco2: "eCO2 (ppm)",
      iaq: "IAQ",
      pm1: "PM1.0",
      pm25: "PM2.5",
      pm10: "PM10",
      noData: "Aucune donnée pour la période"
    },
    logs: {
      title: "Journaux d'événements",
      arcSection: "Événements d'arc",
      tempSection: "Événements de température",
      no: "N°",
      date: "Date",
      time: "Heure",
      sensorId: "ID capteur",
      event: "Événement",
      arcLevel: "Niveau d'arc",
      detail: "Détail",
      tempDetail: "Temp. / Seuil",
      action: "Action",
      arcDetection: "Détection d'arc",
      tempOverheat: "Surchauffe",
      noEvents: "Aucun événement",
      viewGraph: "Détails",
      cycleGraphTitle: "Cycle de température",
      temperature: "Température",
      threshold: "Seuil",
      tempCol: "Temp",
      thrCol: "Thr",
      cycleActive: "Toujours au-dessus du seuil — mise à jour…",
      cycleEnded: "Retour au normal à",
      cycleLoading: "Chargement du cycle…",
      cycleFailed: "Impossible de charger les données du cycle.",
      cycleMissing: "Pas encore de données de cycle pour cet événement. Les nouveaux dépassements après la mise à jour backend sont enregistrés.",
      cycleNoSamples: "Aucun échantillon pour ce cycle.",
      delete: "Supprimer",
      sensor: "Capteur",
      arcData: "Données d'arc",
      confirmDeletion: "Confirmer la suppression",
      confirmDeleteMessage: "Voulez-vous supprimer le journal du capteur",
      at: "à",
      logDeleted: "Journal supprimé",
      deleteFailed: "Échec de la suppression",
      wsError: "Erreur de connexion WebSocket."
    },
    login: {
      errorBothFields: "Veuillez entrer le nom d'utilisateur et le mot de passe",
      errorInvalid: "Nom d'utilisateur ou mot de passe invalide",
      errorFailed: "Échec de la connexion. Réessayez.",
      tempMonitoring: "Surveillance de température",
      irSensor: "Capteur IR",
      envSensor: "Capteur environnement",
      brandSubtitle: "Plateforme de surveillance intelligente",
      welcomeBack: "Bon retour",
      signInSubtitle: "Connectez-vous pour accéder au tableau de bord",
      username: "Nom d'utilisateur",
      password: "Mot de passe",
      signingIn: "Connexion...",
      signIn: "Se connecter",
      loginFooter: "Accès sécurisé au système HAECHIE"
    },
    ai: {
      pageTitle: "ARC Guard AI",
      headerMessage:
        "La détection prédictive IA identifie les défauts d'arc jusqu'à 5 secondes à l'avance.",
      beta: "Bêta",
      streamLive: "Flux d'inférence actif",
      streamConnecting: "Connexion…",
      streamOffline: "Flux hors ligne",
      classifierOutput: "Sortie classificateur",
      arcProbability: "Probabilité d'arc",
      stabilityIndex: "Indice de stabilité",
      edgeLink: "Lien edge",
      riskMeter: "Indice de risque d'arc",
      systemStable: "Dans la plage sûre",
      systemWarning: "Élevé — vérifier le flux",
      edgeContext: "Contexte edge",
      inferenceStream: "Flux d'inférence",
      operationalPlaybook: "Guide opérationnel"
    }
  }
};
